import {Tooltip} from 'antd';

/**
 * Collection of helper utilities for text and string manipulation.
 */
export const textHelpers = {
  /**
   * Returns true if the given string contains any recognised Markdown syntax.
   * @param {*} text - The value to test.
   * @returns {boolean} True if text is a string that contains Markdown patterns.
   * @example
   * textHelpers.containsMarkdown('**bold** text'); // true
   */
  containsMarkdown: (text) => {
    if (typeof text !== 'string') return false;
    const markdownPatterns = [
      /(^|\s)\*{1,2}[^*]+\*{1,2}($|\s)/,         // *italic* or **bold**
      /(^|\s)_[^_]+_($|\s)/,                     // _italic_
      /(^|\n)#{1,6}\s.+/,                        // # Header
      /(^|\n)(\*|-|\+)\s.+/,                     // Unordered list: *, -, +
      /(^|\n)\d+\.\s.+/,                         // Ordered list: 1. item
      /`[^`]+`/,                                 // Inline code: `code`
      /```[\s\S]*?```/,                          // Code block: ``` code ```
      /\[.+\]\(.+\)/,                            // Links: [text](url)
      /^> .+/,                                   // Blockquote: > text
    ];

    return markdownPatterns.some((pattern) => pattern.test(text));
  },
  /**
   * Truncates a string to maxLengthName characters, appending '...' if truncated.
   * @param {string} string - The string to cut.
   * @param {number} [maxLengthName=15] - Maximum character length before truncation.
   * @returns {string} The original string, truncated string with ellipsis, or empty string if falsy.
   * @example
   * textHelpers.cutString('Hello World', 5); // 'Hello...'
   */
  cutString: (string, maxLengthName = 15) =>
    string ? string.length <= maxLengthName ? string : `${string.substring(0, maxLengthName)}...` : '',

  /**
   * Truncates a string to `maxLength` characters and wraps it in an Ant Design Tooltip showing the full text.
   * Non-string values and strings within the limit are returned unchanged.
   * @param {*} string - The value to truncate; non-string values are returned as-is.
   * @param {number} [maxLength=15] - Maximum character count before truncation is applied.
   * @returns {*|string|React.ReactElement} Original value, original string, or a Tooltip-wrapped truncated span.
   * @example
   * textHelpers.cutStringWithTooltip('A very long memo text', 10); // <Tooltip title="A very long memo text"><span>A very lon...</span></Tooltip>
   */
  cutStringWithTooltip: (string, maxLength = 15) => {
    if (typeof string !== 'string' || string.length <= maxLength) return string;
    return (
      <Tooltip title={string}>
        <span>{string.substring(0, maxLength)}...</span>
      </Tooltip>
    );
  },

  /**
   * Returns the character in str2 at the first index where str1 and str2 differ.
   * @param {string} str1 - The reference string.
   * @param {string} str2 - The string to compare against str1.
   * @returns {string|undefined} The differing character from str2, or undefined if strings are identical.
   * @example
   * textHelpers.findStringDifference('abc', 'axc'); // 'x'
   */
  findStringDifference: (str1, str2) => str2[[...str1].findIndex((el, index) => el !== str2[index])],
  /**
   * Capitalizes the first character of a word.
   * @param {string} word - The word to capitalize.
   * @returns {string} The word with its first character uppercased, or the original value if falsy.
   * @example
   * textHelpers.getCapitalized('hello'); // 'Hello'
   */
  getCapitalized: (word) => word ? `${word.charAt(0).toUpperCase()}${word.slice(1)}` : word,
  /**
   * Derives up to two uppercase initials from a full name string.
   * @param {string|null|undefined} string - The full name to derive initials from.
   * @param {boolean} [useOneInitialLetter=false] - When true, returns only the first initial for single-word names.
   * @returns {string} Up to two uppercase initials, or an empty string if input is null or undefined.
   * @example
   * textHelpers.getInitials('John Doe');       // 'JD'
   * textHelpers.getInitials('Alice', true);    // 'A'
   */
  getInitials: (string, useOneInitialLetter = false) => {
    if (string === null || string === undefined) {
      return '';
    }
    let names = string.split(' '),
      initials = names[0].substring(0, 1).toUpperCase(),
      namesLength = names.length;

    if (namesLength > 1) {
      initials += names[namesLength - 1].substring(0, 1).toUpperCase();
    } else if (namesLength === 1 && !useOneInitialLetter) {
      initials += string.slice(1, string.length - 1).substring(0, 1).toUpperCase()
    }
    return initials;
  },
  /**
   * Extracts the filename from a URL string.
   * @param {string} url - A valid URL string.
   * @returns {string|null} The filename portion of the URL path, or null if the URL is invalid.
   * @example
   * textHelpers.getFilenameFromUrl('https://example.com/files/report.pdf'); // 'report.pdf'
   */
  getFilenameFromUrl: (url) => {
    try {
      const cleanUrl = new URL(url);
      const pathname = cleanUrl.pathname;
      return pathname.substring(pathname.lastIndexOf('/') + 1);
    } catch (e) {
      return null;
    }
  },
  /**
   * Reverses a string character by character.
   * @param {string} str - The string to reverse.
   * @returns {string} The reversed string.
   * @example
   * textHelpers.reverse('abc'); // 'cba'
   */
  reverse: (str) => str.split('').reverse().join(''),
  /**
   * Inserts a separator symbol between every group of three characters, starting from the right.
   * @param {string} str - The string to separate.
   * @param {string} separateSymbol - The separator character to insert (e.g. ',' or '.').
   * @returns {string} The string with separators inserted at every third position from the right.
   * @example
   * textHelpers.separateString('1000000', ','); // '1,000,000'
   */
  separateString: (str, separateSymbol) => {
    str = textHelpers.reverse(str);
    str = str.match(/.{1,3}/g).join(' ');
    str = textHelpers.reverse(str);
    if (separateSymbol !== ' ') {
      str = str.replaceAll(' ', separateSymbol);
    }
    return str;
  },
  /**
   * Strips all non-digit characters from a string.
   * @param {string} text - The input string.
   * @returns {string} A string containing only digit characters.
   * @example
   * textHelpers.getTextDigits('+1 (800) 555-0199'); // '18005550199'
   */
  getTextDigits: (text) => text.replace(/\D/g,''),
  /**
   * Splits a full name string into first and last name parts.
   * @param {string} [fullName=''] - The full name to split.
   * @returns {{firstName: string, lastName: string}} Object with firstName and lastName.
   * @example
   * textHelpers.getNameParts('John Doe'); // { firstName: 'John', lastName: 'Doe' }
   */
  getNameParts: (fullName = '') => {
    let parts = fullName.trim().split(' ');
    let firstName = parts[0];
    let lastName = parts.length > 1 ? parts[parts.length - 1] : '';
    return {
      firstName: firstName,
      lastName: lastName
    };
  },
  /**
   * Rounds a numeric value to two decimal places.
   * @param {number|string} val - The value to round.
   * @returns {number|*} The value rounded to two decimals, or the original val if it is NaN.
   * @example
   * textHelpers.roundToTwo(1.2345); // 1.23
   */
  roundToTwo: (val) => {
    const n = Number(val);
    if (Number.isNaN(n)) return val;
    return Number(n.toFixed(2));
  },
  /**
   * Returns truncated text wrapped in a Tooltip when text exceeds maxLength; otherwise returns text as-is.
   * @param {*} text - The value to display; non-string values are returned unchanged.
   * @param {number} [maxLength=20] - Maximum character length before truncation and tooltip are applied.
   * @returns {string|React.ReactElement} Original text, or a Tooltip-wrapped truncated span.
   * @example
   * textHelpers.getTextWithTooltip('A very long description text', 10);
   */
  getTextWithTooltip: (text, maxLength = 20) => {
    if (typeof text !== 'string') return text;
    if (text.length <= maxLength) return text;
    const truncatedText = text.substring(0, maxLength) + '...';
    return (
      <Tooltip title={text} classNames={{root: 'tooltip-with-text'}}>
        <span>{truncatedText}</span>
      </Tooltip>
    );
  }
}
